/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import { CircleCheck, CircleX, Clock, Cloud, LoaderCircle } from "lucide-react";

import { ProviderBadge, providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import IconTile from "@src/components/ui/IconTile";
import {
  Cell,
  FlexCell,
  ListRow,
  Muted,
  Pill,
  Primary,
  Secondary,
  Stack,
  formatDateTime,
  formatShortDateTime,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

import type { Endpoint } from "@src/@types/Endpoint";

export const ENDPOINT_COLUMNS: ListColumn[] = [
  { key: "name", label: "Name", width: "minmax(240px, 2.4fr)" },
  { key: "provider", label: "Provider", width: "128px" },
  { key: "region", label: "Region", width: "minmax(120px, 1fr)" },
  { key: "usage", label: "Usage", width: "150px" },
  { key: "created", label: "Created", width: "108px" },
  { key: "validation", label: "Validation", width: "132px" },
];

/** The last validation of an endpoint in this browser. */
export type EndpointCheck = {
  state: "valid" | "failed" | "checking";
  at?: string;
  message?: string;
};

export type EndpointUsage = {
  replicasCount: number;
  migrationsCount: number;
};

/** "2 replicas · 1 migration", or "" when the endpoint is not used. */
export const usageText = (usage: EndpointUsage) => {
  const parts: string[] = [];
  if (usage.replicasCount) {
    parts.push(`${usage.replicasCount} replica${usage.replicasCount === 1 ? "" : "s"}`);
  }
  if (usage.migrationsCount) {
    parts.push(`${usage.migrationsCount} migration${usage.migrationsCount === 1 ? "" : "s"}`);
  }
  return parts.join(" · ");
};

/** The endpoint's region(s): the Swift regions it is mapped to, else its own region setting. */
export const endpointRegion = (endpoint: Endpoint, regionName?: (id: string) => string | undefined) => {
  const mapped = (endpoint.mapped_regions || []).map(id => regionName?.(id) || id).filter(Boolean);
  if (mapped.length) return mapped.join(", ");
  const ci = endpoint.connection_info || {};
  return String(ci.region || ci.location || ci.region_name || "");
};

type Props = {
  item: Endpoint;
  onClick: () => void;
  selected: boolean;
  selectable?: boolean;
  onSelectedChange: (value: boolean) => void;
  getUsage: (item: Endpoint) => EndpointUsage;
  getRegionName?: (id: string) => string | undefined;
  check?: EndpointCheck | null;
  actions?: DropdownAction[];
};

export const ValidationPill = ({ check }: { check?: EndpointCheck | null }) => {
  if (!check) {
    return (
      <Pill tone="neutral" icon={Clock} title="Not validated from this browser yet">
        Not checked
      </Pill>
    );
  }
  if (check.state === "checking") {
    return (
      <Pill tone="info" icon={LoaderCircle} spin>
        Validating
      </Pill>
    );
  }
  const when = check.at ? ` (${timeAgo(check.at)})` : "";
  if (check.state === "valid") {
    return (
      <Pill tone="success" icon={CircleCheck} title={`Validated${when}`}>
        Valid
      </Pill>
    );
  }
  return (
    <Pill tone="danger" icon={CircleX} title={`${check.message || "Validation failed"}${when}`}>
      Failed
    </Pill>
  );
};

@observer
class EndpointListItem extends React.Component<Props> {
  render() {
    const { item } = this.props;
    const usage = this.props.getUsage(item);
    const usageLabel = usageText(usage);
    const region = endpointRegion(item, this.props.getRegionName);
    return (
      <ListRow
        columns={ENDPOINT_COLUMNS}
        selectable={this.props.selectable}
        selected={this.props.selected}
        onSelectedChange={this.props.onSelectedChange}
        onClick={this.props.onClick}
        actions={this.props.actions}
        testId="endpointListItem"
      >
        <FlexCell gap={12}>
          <IconTile icon={Cloud} size={28} />
          <Stack>
            <Primary title={item.name}>{item.name}</Primary>
            <Secondary title={item.description || ""}>
              {item.description || <Muted>No description</Muted>}
            </Secondary>
          </Stack>
        </FlexCell>
        <FlexCell title={providerLabel(item.type)}>
          <ProviderBadge provider={item.type} />
        </FlexCell>
        <Cell title={region}>{region || <Muted>—</Muted>}</Cell>
        <Cell title={usageLabel || "Not used by any transfer"}>
          {usageLabel || <Muted>Unused</Muted>}
        </Cell>
        <Cell title={formatDateTime(item.created_at)}>
          {formatShortDateTime(item.created_at)}
        </Cell>
        <div>
          <ValidationPill check={this.props.check} />
        </div>
      </ListRow>
    );
  }
}

export default EndpointListItem;
